import React, { useState, useRef, useEffect } from "react";
import { Typography, Grid, Box, Button, TextField, IconButton, Checkbox, Link, useMediaQuery } from "@material-ui/core";
import { makeStyles, withStyles, useTheme } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import ThumbUpAltOutlinedIcon from '@material-ui/icons/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@material-ui/icons/ThumbDownAltOutlined';
import Skeleton from '@material-ui/lab/Skeleton';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import ExerciseImage from './ExerciseImage';

const useStyles = makeStyles((theme) => ({
  wideButton: {
    background: '#fb741a',
    color: '#fff',
    fontWeight: 700,
    padding: '15px 10px',
    marginRight: 0,
    borderRadius: 18,
    marginBlock: 1,
    boxShadow: "none",
    width: '95%',
    transition: 'background-color 200ms ease, transform 120ms ease',
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontSize: 'clamp(11px, 0.9vw, 15px)',
    '&:hover': {
      background: '#002D55'
    },
    '&:active': {
      background: '#002D55',
      transform: 'translateY(1px)'
    }
  },
  inputBlue: {
    color: '#002D55',
    fontSize: 'clamp(12px, 0.9vw, 14px)',
  },
  labelBlue: {
    color: '#002D55 !important',
    fontSize: 'clamp(15px, 0.9vw, 16px)',
  },
  root: {
    height: '100%',
    width: '100%',
    // padding, minWidth, minHeight moved to Card for consistent border design
  },
  exerciseTitle: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(9px, 0.9vw, 14px)',
    marginBottom: 2,
    textAlign: 'center',
    fontFamily: 'Montserrat, Arial, sans-serif',
    letterSpacing: 0.1,
  },
  title: {
    color: '#002D55',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '0.5%',
    marginTop: 0,
    fontSize: 'clamp(18px, 1.5vw, 26px)',
    [theme.breakpoints.down('lg')]: { fontSize: '20px' },
    [theme.breakpoints.down('md')]: { fontSize: '18px' },
    [theme.breakpoints.down('sm')]: { fontSize: '16px' },
    [theme.breakpoints.down('xs')]: { fontSize: '14px' },
  },
  subtitle: {
    color: '#002D55',
    fontWeight: 'normal',
    textAlign: 'center',
    marginBottom: '1%',
    fontSize: 'clamp(14px, 1.4vw, 22px)',
    [theme.breakpoints.down('lg')]: { fontSize: '18px' },
    [theme.breakpoints.down('md')]: { fontSize: '16px' },
    [theme.breakpoints.down('sm')]: { fontSize: '14px' },
    [theme.breakpoints.down('xs')]: { fontSize: '13px' },
  },
  subtitle2: {
    color: '#002D55',
    fontWeight: 'normal',
    textAlign: 'left',
    marginRight: 20,
    marginBottom: 0,
    fontSize: 'clamp(14px, 1.3vw, 20px)',
    [theme.breakpoints.down('lg')]: { fontSize: '16px' },
    [theme.breakpoints.down('md')]: { fontSize: '14px' },
    [theme.breakpoints.down('sm')]: { fontSize: '13px' },
    [theme.breakpoints.down('xs')]: { fontSize: '12px' },
  },
  iconButton: {
    marginRight: 16,
    border: "1.5px solid #ccc",
    backgroundColor: "#f5f5f5",
    color: "#fb741a",
    fontSize: 'clamp(18px, 1.8vw, 28px)',
    [theme.breakpoints.down('lg')]: {
      padding: 10,
      fontSize: '24px',
    },
    [theme.breakpoints.down('md')]: {
      padding: 8,
      fontSize: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      padding: 6,
      fontSize: '18px',
    },
    [theme.breakpoints.down('xs')]: {
      padding: 4,
      fontSize: '16px',
    },
  },
  exercisesRow: {
    flexWrap: "nowrap",
    overflowX: "auto",
    marginBottom: 24,
    [theme.breakpoints.down('lg')]: {
      marginBottom: 20,
    },
    [theme.breakpoints.down('md')]: {
      marginBottom: 16,
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: 12,
    },
    [theme.breakpoints.down('xs')]: {
      marginBottom: 8,
    },
  },
  exerciseCard: {
    minWidth: 180,
    maxWidth: 220,
    marginRight: 14,
    background: 'rgba(255,255,255,0.75)',
    borderRadius: 18,
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
    position: "relative",
    [theme.breakpoints.down('lg')]: {
      minWidth: 140,
      maxWidth: 150,
      marginRight: 10,
    },
    [theme.breakpoints.down('md')]: {
      minWidth: 130,
      maxWidth: 140,
      marginRight: 8,
    },
    [theme.breakpoints.down('sm')]: {
      minWidth: 125,
      maxWidth: 125,
      marginRight: 6,
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: 110,
      maxWidth: 125,
      marginRight: 4,
    },
  },
  exerciseImg: {
    height: 80,
    width: 120,
    objectFit: "cover",
    borderRadius: 14,
    marginBottom: 8,
    [theme.breakpoints.down('lg')]: {
      height: 70,
      width: 105,
    },
    [theme.breakpoints.down('md')]: {
      height: 60,
      width: 90,
    },
    [theme.breakpoints.down('sm')]: {
      height: 50,
      width: 75,
    },
    [theme.breakpoints.down('xs')]: {
      height: 40,
      width: 60,
    },
  },
  checkbox: {
    position: "absolute",
    top: -5,
    left: -5,
    color: "#002D55",
    // control the actual icon size by targeting the rendered svg
    '& svg': {
      fontSize: 28,
    },
    [theme.breakpoints.down('lg')]: {
      top: -4,
      left: -4,
      '& svg': {
        fontSize: 24,
      }
    },
    [theme.breakpoints.down('md')]: {
      top: -3,
      left: -3,
      '& svg': {
        fontSize: 20,
      }
    },
    [theme.breakpoints.down('sm')]: {
      top: -2,
      left: -2,
      '& svg': {
        fontSize: 18,
      }
    },
    [theme.breakpoints.down('xs')]: {
      top: -1,
      left: -1,
      '& svg': {
        fontSize: 16,
      }
    },
  },
  likeButton: {
    marginRight: 16,
    border: "1.5px solid #ccc",
    backgroundColor: "#f5f5f5",
    color: "#888",
    "&.selected": {
      backgroundColor: "#002D55",
      borderColor: "#002D55",
      color: "#fff"
    }
  },
  dislikeButton: {
    border: "1.5px solid #ccc",
    backgroundColor: "#f5f5f5",
    color: "#888",
    "&.selected": {
      backgroundColor: "#002D55",
      borderColor: "#002D55",
      color: "#fff"
    }
  },
  continueButton: {
    marginTop: 24,
    minWidth: 180,
    fontSize: 'clamp(12px, 1vw, 16px)',
    backgroundColor: "#ccc",
    borderColor: "#ccc",
    color: "#fff",
    "&.enabled": {
      backgroundColor: "#002D55",
      borderColor: "#002D55",
      color: "#fff"
    }
  },
  feedbackField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 50,
      fontSize: 'clamp(10px, 0.85vw, 13px)',
      '&.Mui-focused fieldset': {
        borderColor: '#fb741a',
      },
      '& fieldset': {
        borderRadius: 18,
      },
      '& input': {
        padding: '0 clamp(8px, 0.7vw, 12px)',
        height: '100%',
        boxSizing: 'border-box',
        fontSize: 'clamp(10px, 0.85vw, 13px)',
        /* hide number spinners */
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          '-webkit-appearance': 'none',
          margin: 0,
        },
        '&[type=number]': {
          '-moz-appearance': 'textfield',
        },
      },
    },
    '& .MuiInputLabel-outlined': {
      fontSize: 'clamp(10px, 0.85vw, 13px)',
      transform: 'translate(clamp(8px, 0.7vw, 12px), clamp(6px, 0.55vw, 9px)) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#fb741a',
    },
  },
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 50,
      height: 'clamp(22px, 2.2vw, 30px)',
      fontSize: 'clamp(10px, 0.85vw, 13px)',
      '&.Mui-focused fieldset': {
        borderColor: '#fb741a',
      },
      '& fieldset': {
        borderRadius: 50,
      },
      '& input': {
        padding: '0 clamp(8px, 0.7vw, 12px)',
        height: '100%',
        boxSizing: 'border-box',
        fontSize: 'clamp(10px, 0.85vw, 13px)',
        /* hide number spinners */
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          '-webkit-appearance': 'none',
          margin: 0,
        },
        '&[type=number]': {
          '-moz-appearance': 'textfield',
        },
      },
    },
    '& .MuiInputLabel-outlined': {
      fontSize: 'clamp(10px, 0.85vw, 13px)',
      transform: 'translate(clamp(8px, 0.7vw, 12px), clamp(6px, 0.55vw, 9px)) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
      },
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#fb741a',
    },
  },
}));

const ContinueButton = withStyles((theme) => ({
  root: {
    boxShadow: "none",
    textTransform: "none",
    fontSize: 'clamp(12px, 1vw, 16px)',
    padding: "17px 10px",
    border: "0.5px solid",
    lineHeight: 0.5,
    marginTop: "1%",
    borderRadius: 50,
    backgroundColor: "#ccc",
    borderColor: "#ccc",
    color: "#FFFFFF",
    "&.enabled": {
      backgroundColor: "#fb741a",
      color: "#FFF"
    },
    "&.enabled:hover": {
      backgroundColor: "#002D55",
      color: "#FFF"
    },
    "&.Mui-disabled": {
      backgroundColor: "#ccc",
      color: "#fff",
      opacity: 1,
    },
    [theme.breakpoints.down('lg')]: {
      padding: "16px 12px",
    },
    [theme.breakpoints.down('md')]: {
      padding: "14px 30px",
    },
    [theme.breakpoints.down('sm')]: {
      padding: "15px 8px",
    },
    [theme.breakpoints.down('xs')]: {
      padding: "13px 8px",
    },
  }
}))(Button);

const SkipButton = withStyles((theme) => ({
  root: {
    boxShadow: "none",
    textTransform: "none",
    fontSize: 'clamp(12px, 1vw, 16px)',
    padding: "17px 10px",
    border: "0.5px solid",
    lineHeight: 0.5,
    marginTop: "1%",
    marginLeft: 5,
    borderRadius: 50,
    backgroundColor: "#ccc",
    borderColor: "#ccc",
    color: "#FFFFFF",
    "&:hover": {
      backgroundColor: "#002D55",
      color: "#FFF"
    },
    [theme.breakpoints.down('lg')]: {
      padding: "16px 12px",
    },
    [theme.breakpoints.down('md')]: {
      padding: "14px 30px",
    },
    [theme.breakpoints.down('sm')]: {
      padding: "15px 8px",
    },
    [theme.breakpoints.down('xs')]: {
      padding: "13px 8px",
    },
  }
}))(Button);

const SessionSummary = ({
  open,
  anchor,
  container,
  exercises,
  expectedExerciseLength,
  feedbackIds,
  token,
  completionOnly = false,
  onClose,
  onContinue,
  onSkip
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('xs'));
  const isSm = useMediaQuery(theme.breakpoints.down('sm'));
  const sessionExerciseCount = Math.max(1, expectedExerciseLength || exercises.length || 1);
  const estimatedCardWidth = isSm ? 125 : isXs ? 125 : 180;
  const estimatedGapWidth = isSm ? 6 : 14;
  const estimatedSummaryWidth = Math.max(
    600,
    (sessionExerciseCount * estimatedCardWidth) + (Math.max(sessionExerciseCount - 1, 0) * estimatedGapWidth) + 56
  );
  const summaryWidth = isSm ? 'calc(100vw - 16px)' : `min(75vw, ${estimatedSummaryWidth}px)`;
  const [likeStatus, setLikeStatus] = useState(null); // "like" | "dislike" | null
  const [feedback, setFeedback] = useState("");
  const feedbackRef = useRef(null);
  // Per-exercise completion edit state. `editingId` is the exercise.identifier
  // currently being edited inline; `completion[id]` holds the latest values
  // patched to the backend (or pulled from the original prescription).
  const [editingId, setEditingId] = useState(null);
  const [completion, setCompletion] = useState({});
  const [savingId, setSavingId] = useState(null);

  const beginEdit = (ex) => {
    const id = ex.identifier;
    if (!id || !feedbackIds || !feedbackIds[id]) return; // no row to PATCH
    setEditingId(id);
    setCompletion(prev => prev[id] ? prev : {
      ...prev,
      [id]: {
        sets: ex.completed_sets ?? ex.sets ?? "",
        repetitions: ex.completed_repetitions ?? ex.repetitions ?? "",
        time: ex.completed_time ?? ex.time ?? "",
        saved: ex.completed_sets != null || ex.completed_repetitions != null || ex.completed_time != null,
      },
    });
  };

  const updateField = (id, field, value) => {
    setCompletion(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const saveEdit = async (ex) => {
    const id = ex.identifier;
    const fid = feedbackIds && feedbackIds[id];
    if (!fid) return;
    const vals = completion[id] || {};
    const toIntOrNull = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = parseInt(v, 10);
      return Number.isNaN(n) ? null : n;
    };
    // Only PATCH the dose-type-relevant completion field so reps aren't recorded
    // for time-based exercises (and vice versa).
    const useTimeDose = (ex.doseType
      ? ex.doseType === 'time'
      : (ex.time != null && ex.time > 0));
    const payload = {};
    const cs = toIntOrNull(vals.sets);
    const cr = toIntOrNull(vals.repetitions);
    const ct = toIntOrNull(vals.time);
    if (cs !== null) payload.completed_sets = cs;
    if (!useTimeDose && cr !== null) payload.completed_repetitions = cr;
    if (useTimeDose && ct !== null) payload.completed_time = ct;
    setSavingId(id);
    try {
      const resp = await fetch(`https://api.virtus-one.com/treatment/feedback/${fid}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        const data = await resp.json();
        setCompletion(prev => ({
          ...prev,
          [id]: {
            sets: data.completed_sets ?? "",
            repetitions: data.completed_repetitions ?? "",
            time: data.completed_time ?? "",
            saved: true,
          },
        }));
        setEditingId(null);
      } else {
        alert('Could not update completion');
      }
    } catch (e) {
      alert('Network error updating completion');
    } finally {
      setSavingId(null);
    }
  };

  useEffect(() => {
    if (likeStatus === "dislike" && feedbackRef.current) {
      feedbackRef.current.focus();
    }
  }, [likeStatus]);

  // Button enable logic
  const canContinue =
    (likeStatus === "like") ||
    (likeStatus === "dislike" && feedback.trim().length >= 20);

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={{
        top: window.innerHeight / 2,
        left: window.innerWidth / 2
      }}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
      }}
      PaperProps={{ style: { borderRadius: 18, width: summaryWidth, maxWidth: '100vw' } }}
    >
      <div
        style={{
          background: "linear-gradient(220deg, rgba(185, 204, 231, 0.55), #6a8caf)",
          borderRadius: 18,
          padding: 8,
          minWidth: 280,
          maxWidth: summaryWidth,
          width: summaryWidth,
          margin: '0 auto',
          boxSizing: 'border-box',
        }}
      >
        <div
          className={classes.root}
          style={{
            background: 'linear-gradient(90deg, #dce4eb, #cfd1d7ff)',
            borderRadius: 18,
            padding: 16,
            maxWidth: summaryWidth,
            width: '100%',
            margin: '0 auto',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            variant="h5"
            className={classes.title}
          >
            Session Complete!
          </Typography>
          {completionOnly && (
            <IconButton
              aria-label="Close session review"
              onClick={onClose}
              style={{ position: 'absolute', top: 12, right: 12, color: '#002D55' }}
            >
              <CloseIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            className={classes.subtitle}
          >
            You completed {sessionExerciseCount} exercises today:
          </Typography>
          <Grid
            container
            direction="row"
            className={classes.exercisesRow}
            wrap="nowrap"
            style={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch', paddingBottom: 8 }}
          >
            {exercises.map((exercise, idx) => {
              const id = exercise.identifier;
              const fid = feedbackIds && id ? feedbackIds[id] : null;
              const editable = !!fid;
              const isEditing = editingId === id;
              const saved = (completion[id] && completion[id].saved)
                || exercise.completed_sets != null
                || exercise.completed_repetitions != null
                || exercise.completed_time != null;
              const current = completion[id] || {};
              // Mirror popoverFeedback's inference: explicit doseType wins,
              // otherwise non-zero time means time-based, else repetitions-based.
              const useTimeDose = (exercise.doseType
                ? exercise.doseType === 'time'
                : (exercise.time != null && exercise.time > 0));
              // Always render a per-card completion summary so the patient can
              // verify what was recorded. When `saved` is true we show the
              // patient-edited values; otherwise we default to the prescribed
              // dose (which is what the backend stores when the patient skips
              // the partial-completion form).
              const displaySets = saved ? (current.sets ?? exercise.completed_sets ?? exercise.sets) : exercise.sets;
              const displayReps = saved ? (current.repetitions ?? exercise.completed_repetitions ?? exercise.repetitions) : exercise.repetitions;
              const displayTime = saved ? (current.time ?? exercise.completed_time ?? exercise.time) : exercise.time;
              const partial =
                (exercise.sets != null && displaySets != null && displaySets < exercise.sets) ||
                (!useTimeDose && exercise.repetitions != null && displayReps != null && displayReps < exercise.repetitions) ||
                (useTimeDose && exercise.time != null && displayTime != null && displayTime < exercise.time);
              const showCompletedBadge = saved && partial;
              return (
              <Grid item key={exercise.identifier || idx} className={classes.exerciseCard}
                onClick={() => { if (editable && !isEditing) beginEdit(exercise); }}
                style={{ cursor: editable ? 'pointer' : 'default' }}
                title={editable ? 'Tap to record partial completion' : undefined}
              >
                <Checkbox
                  checked
                  className={classes.checkbox}
                  checkedIcon={<CheckCircleIcon style={{ color: "#fb741a" }}/>} 
                  disabled
                />
                <ExerciseImage exercise={exercise} className={classes.exerciseImg} skeletonWidth={120} skeletonHeight={80} />
                <Typography className={classes.exerciseTitle} title={exercise.title}>
                  {exercise.title ? (exercise.title.length > 40 ? exercise.title.slice(0, 40).trim() + '…' : exercise.title) : ''}
                </Typography>
                {!isEditing && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 4, width: '100%' }}>
                    <Typography style={{
                      color: showCompletedBadge ? '#c0392b' : '#324158',
                      fontWeight: 600,
                      fontSize: 'clamp(9px, 0.7vw, 11px)',
                      textAlign: 'center',
                    }}>
                      {(() => {
                        const bits = [];
                        if (exercise.sets != null) bits.push(`${displaySets ?? 0}/${exercise.sets} sets`);
                        if (!useTimeDose && exercise.repetitions != null && exercise.repetitions > 0) {
                          bits.push(`${displayReps ?? 0}/${exercise.repetitions} reps`);
                        }
                        if (useTimeDose && exercise.time != null && exercise.time > 0) {
                          bits.push(`${displayTime ?? 0}/${exercise.time}s`);
                        }
                        return bits.join(' • ');
                      })()}
                    </Typography>
                    {editable && (
                      <Link
                        component="button"
                        type="button"
                        onClick={(e) => { e.stopPropagation(); beginEdit(exercise); }}
                        style={{ color: '#fb741a', fontWeight: 600, fontSize: 'clamp(9px, 0.7vw, 11px)', marginTop: 2 }}
                      >
                        {saved ? 'Edit' : 'Adjust completion'}
                      </Link>
                    )}
                  </div>
                )}
                {isEditing && (
                  <div onClick={e => e.stopPropagation()} style={{ width: '100%', marginTop: 'clamp(8px, 0.8vw, 16px)', display: 'flex', flexDirection: 'column', gap: 'clamp(4px, 0.4vw, 6px)' }}>
                    <div style={{ display: 'flex', flexDirection: isSm ? 'column' : 'row', 
                      
                     }}>
                    {exercise.sets != null && (
                      <TextField label={`Sets/${exercise.sets}`} type="number" size="small" variant="outlined"
                        className={classes.orangeFocused}
                        style={{ flex: 1, minWidth: 0, paddingBottom: 12, marginRight: isSm ? 0 : 'clamp(4px, 0.4vw, 6px)', gap: 'clamp(4px, 0.4vw, 6px)'  }}
                        value={current.sets ?? ""}
                        onChange={e => updateField(id, 'sets', e.target.value)}
                        InputProps={{ inputProps: { min: 0, max: exercise.sets } }} />
                    )}
                    {!useTimeDose && exercise.repetitions != null && exercise.repetitions > 0 && (
                      <TextField label={`Reps/${exercise.repetitions}`} type="number" size="small" variant="outlined"
                        className={classes.orangeFocused}
                        style={{ flex: 1, minWidth: 0, paddingBottom: 12, flex: 1, minWidth: 0, gap: 'clamp(4px, 0.4vw, 6px)'  }}
                        value={current.repetitions ?? ""}
                        onChange={e => updateField(id, 'repetitions', e.target.value)}
                        InputProps={{ inputProps: { min: 0, max: exercise.repetitions } }} />
                    )}
                    {useTimeDose && exercise.time != null && exercise.time > 0 && (
                      <TextField label={`Sec/${exercise.time}`} type="number" size="small" variant="outlined"
                        className={classes.orangeFocused}
                        style={{ flex: 1, minWidth: 0, paddingBottom: 12, flex: 1, minWidth: 0, gap: 'clamp(4px, 0.4vw, 6px)'  }}
                        value={current.time ?? ""}
                        onChange={e => updateField(id, 'time', e.target.value)}
                        InputProps={{ inputProps: { min: 0, max: exercise.time } }} />
                    )}
                    </div>
                    <div style={{ display: 'flex', gap: 'clamp(4px, 0.4vw, 6px)', marginTop: 'clamp(4px, 0.5vw, 8px)' }}>
                      {isXs ? (
                        <IconButton disabled={savingId === id}
                          style={{ background: '#fb741a', color: '#fff', borderRadius: 50, boxShadow: 'none', padding: 6, flex: 1, minWidth: 0, height: 'clamp(23px, 2.2vw, 31px)' }}
                          onClick={() => saveEdit(exercise)}>
                          {savingId === id ? <span style={{ fontSize: 10 }}>...</span> : <CheckIcon style={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }} />}
                        </IconButton>
                      ) : (
                        <Button variant="contained" disabled={savingId === id}
                          style={{ background: '#fb741a', color: '#fff', flex: 1, minWidth: 0, fontSize: 'clamp(9px, 0.75vw, 12px)', borderRadius: 50, boxShadow: 'none', height: 'clamp(23px, 2.2vw, 31px)' }}
                          onClick={() => saveEdit(exercise)}>
                          {savingId === id ? '...' : 'Save'}
                        </Button>
                      )}
                      {isXs ? (
                        <IconButton
                          style={{ borderRadius: 50, border: '1px solid rgba(0,0,0,0.23)', padding: 6, flex: 1, minWidth: 0, height: 'clamp(23px, 2.2vw, 31px)' }}
                          onClick={() => setEditingId(null)}>
                          <CloseIcon style={{ fontSize: 'clamp(14px, 1.2vw, 18px)' }} />
                        </IconButton>
                      ) : (
                        <Button variant="outlined" style={{ flex: 1, minWidth: 0, fontSize: 'clamp(9px, 0.75vw, 12px)', borderRadius: 50, height: 'clamp(23px, 2.2vw, 31px)' }}
                          onClick={() => setEditingId(null)}>
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </Grid>
              );
            })}
          </Grid>

          {!completionOnly && <>
          <Grid container direction="row" alignItems="center" style={{ marginTop: "2.5%", marginBottom: "2%", width: "100%", justifyContent: "center" }}>
            <Typography variant="h6" className={classes.subtitle2}>
              How did you feel about this session?
            </Typography>
            <IconButton
              className={`${classes.likeButton} ${classes.iconButton} ${likeStatus === "like" ? "selected" : ""}`}
              onClick={() => setLikeStatus("like")}
            >
              <ThumbUpAltOutlinedIcon fontSize="inherit" />
            </IconButton>
            <IconButton
              className={`${classes.dislikeButton} ${classes.iconButton} ${likeStatus === "dislike" ? "selected" : ""}`}
              onClick={() => setLikeStatus("dislike")}
            >
              <ThumbDownAltOutlinedIcon fontSize="inherit" />
            </IconButton>
          </Grid>

          <TextField
            label={likeStatus === "dislike" ? "Feedback (required if you disliked the session)" : "Feedback (optional)"}
            multiline
            rows={2}
            variant="outlined"
            value={feedback}
            onChange={e => setFeedback(e.target.value)}
            className={classes.feedbackField}
            style={{ width: "100%", marginTop: 10, fontSize: 10 }}
            //disabled={likeStatus !== "dislike" }
            required={likeStatus === "dislike"}
            //error={likeStatus === "dislike" && feedback.trim().length > 0 && feedback.trim().length < 20}
            error={
              likeStatus === "dislike" &&
              feedback.trim().length > 0 &&
              (feedback.trim().split(' ').length < 5 || (feedback.trim().match(/ /g) || []).length < 4)
              && (feedback.trim().length < 18)
            }
            helperText={
              likeStatus === "dislike" &&
              feedback.trim().length > 0 &&
              (
                (feedback.trim().split(' ').length < 5 || (feedback.trim().match(/ /g) || []).length < 4)
                || feedback.trim().length < 18
              )
                ? "Please better describe your experience."
                : ""
            }
            inputRef={feedbackRef}
            InputProps={{
              classes: {
                input: classes.inputBlue,
                ...(likeStatus === "dislike" ? { notchedOutline: "orange-border" } : {})
              }
            }}
            InputLabelProps={{
              classes: { root: classes.labelBlue }
            }}
          />
          <style>{`
            .orange-border {
              border-color: orange !important;
            }
          `}</style>

          <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', width: '100%', marginTop: "0.5%", gap: 4 }}>
            <ContinueButton
              className={canContinue ? `enabled ${classes.wideButton}` : classes.wideButton}
              disabled={!canContinue}
              onClick={() => {
                onContinue({ likeStatus, feedback });
              }}
              style={{ width: '48%', marginTop: 12}}
            >
              CONTINUE
            </ContinueButton>
            <SkipButton
              className={`enabled ${classes.wideButton}`}
              onClick={onSkip}
              style={{ width: '48%', marginTop: 12 }}
            >
              SKIP
            </SkipButton>
          </div>
          </>}
        </div>
      </div>
    </Popover>
  );
};

export default SessionSummary;